import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, UserPlus } from "@phosphor-icons/react";
import api from "@/lib/api";

const fmtDay = (iso) => new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const inputCls = "w-full bg-black border border-white/15 px-3 py-2 text-sm font-mono focus:border-[#FFD300] outline-none";
const labelCls = "overline block mb-1";

export const PAYMENT_METHODS = [
  ["cash", "Cash"], ["card_in_person", "Card at venue"], ["zelle", "Zelle"], ["cashapp", "Cash App"],
  ["venmo", "Venmo"], ["check", "Check"], ["other", "Other"],
];

export const STATUS_LABELS = { scheduled: "Scheduled", completed: "Completed", no_show: "No-show", cancelled: "Cancelled" };
export const STATUS_CLASS = {
  scheduled: "bg-white/10 text-neutral-300",
  completed: "bg-emerald-500/20 text-emerald-300",
  no_show: "bg-orange-500/20 text-orange-300",
  cancelled: "bg-red-500/20 text-red-300",
};

export function Modal({ title, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 pt-16" onClick={onClose} data-testid={testId}>
      <div className="surface-card w-full max-w-lg bg-[#0A0A0A] border border-white/15" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="font-heading text-xl tracking-tight">{title}</div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="modal-close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

export function useSessions() {
  const [sessions, setSessions] = useState([]);
  useEffect(() => { api.get("/admin/sessions").then(({ data }) => setSessions(data || [])).catch(() => {}); }, []);
  return sessions;
}

export function SessionPicker({ sessions, courseId, value, onChange, allowNone, testId = "session-select" }) {
  const options = useMemo(() => sessions.filter((s) => s.course_id === courseId && new Date(s.starts_at) > new Date())
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at)), [sessions, courseId]);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId}>
      <option value="">{allowNone ? "No date yet (assign later)" : "— pick a class date —"}</option>
      {options.map((s) => {
        const left = Math.max(0, (s.capacity || 8) - (s.enrolled_count || 0));
        return <option key={s.id} value={s.id}>{fmtDay(s.starts_at)} · {left === 0 ? "FULL" : `${left} seat${left === 1 ? "" : "s"} left`} · {(s.venue || "").split(" — ")[0]}</option>;
      })}
      {courseId && options.length === 0 && <option disabled>No upcoming dates for this course</option>}
    </select>
  );
}

function isFull(sessions, id) {
  const s = sessions.find((x) => x.id === id);
  return s ? (s.enrolled_count || 0) >= (s.capacity || 8) : false;
}

export function AddStudentModal({ onClose, onDone }) {
  const sessions = useSessions();
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ user_id: "", course_id: "", class_session_id: "", payment: "paid_in_person", method: "cash", amount: "", note: "", override_capacity: false, send_email: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data || []));
    api.get("/courses").then(({ data }) => setCourses(data || []));
  }, []);
  const course = courses.find((c) => c.id === f.course_id);
  const matches = users.filter((u) => !q || `${u.name} ${u.email} ${u.phone || ""}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8);
  const full = f.class_session_id && isFull(sessions, f.class_session_id);
  const needsAmount = f.payment === "paid_in_person" || f.payment === "deposit_in_person";
  const defaultAmt = course ? (f.payment === "deposit_in_person" ? course.price / 2 : course.price) : 0;

  const submit = async () => {
    if (!f.user_id || !f.course_id) { toast.error("Pick a student and a course"); return; }
    if (full && !f.override_capacity) { toast.error("Class is full — tick override to add a 9th seat"); return; }
    setBusy(true);
    try {
      await api.post("/admin/enrollments", {
        ...f, class_session_id: f.class_session_id || null,
        amount_cents: needsAmount && f.amount !== "" ? Math.round(Number(f.amount) * 100) : null,
      });
      toast.success("Student added to course");
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add student"); } finally { setBusy(false); }
  };

  return (
    <Modal title="Add student to a course" onClose={onClose} testId="add-student-modal">
      <div>
        <label className={labelCls}>Student</label>
        <input value={q} onChange={(e) => { setQ(e.target.value); setF({ ...f, user_id: "" }); }} placeholder="Search name, email or phone…" className={inputCls} data-testid="add-student-search" />
        {q && !f.user_id && (
          <div className="border border-white/10 border-t-0 max-h-48 overflow-y-auto" data-testid="add-student-results">
            {matches.length === 0 && <div className="p-2 text-xs text-neutral-500">No matching accounts — the student must register first.</div>}
            {matches.map((u) => (
              <button key={u.id} onClick={() => { setF({ ...f, user_id: u.id }); setQ(`${u.name} <${u.email}>`); }} className="w-full text-left p-2 hover:bg-white/5 text-sm" data-testid={`add-student-pick-${u.id}`}>
                <span className="text-white">{u.name}</span> <span className="text-neutral-500 font-mono text-xs">{u.email}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div>
        <label className={labelCls}>Course</label>
        <select value={f.course_id} onChange={(e) => setF({ ...f, course_id: e.target.value, class_session_id: "" })} className={inputCls} data-testid="add-student-course">
          <option value="">— pick a course —</option>
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title} · ${c.price}</option>)}
        </select>
      </div>
      {f.course_id && (
        <div>
          <label className={labelCls}>Class date</label>
          <SessionPicker sessions={sessions} courseId={f.course_id} value={f.class_session_id} onChange={(v) => setF({ ...f, class_session_id: v })} allowNone testId="add-student-session" />
          {full && (
            <label className="flex items-center gap-2 mt-2 text-xs text-orange-300" data-testid="add-student-override">
              <input type="checkbox" checked={f.override_capacity} onChange={(e) => setF({ ...f, override_capacity: e.target.checked })} />
              This class is full (8/8) — add a 9th seat anyway
            </label>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Payment</label>
          <select value={f.payment} onChange={(e) => setF({ ...f, payment: e.target.value, amount: "" })} className={inputCls} data-testid="add-student-payment">
            <option value="paid_in_person">Paid in full (in person)</option>
            <option value="deposit_in_person">Deposit (in person)</option>
            <option value="comp">Comp / free</option>
            <option value="unpaid">Unpaid — owes full price</option>
          </select>
        </div>
        {needsAmount && (
          <div>
            <label className={labelCls}>Method</label>
            <select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} className={inputCls} data-testid="add-student-method">
              {PAYMENT_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        )}
      </div>
      {needsAmount && (
        <div>
          <label className={labelCls}>Amount collected ($)</label>
          <input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder={defaultAmt.toFixed(2)} className={inputCls} data-testid="add-student-amount" />
        </div>
      )}
      <div>
        <label className={labelCls}>Note (optional)</label>
        <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={inputCls} placeholder="e.g. paid cash at the range on 11/02" data-testid="add-student-note" />
      </div>
      <label className="flex items-center gap-2 text-xs text-neutral-300">
        <input type="checkbox" checked={f.send_email} onChange={(e) => setF({ ...f, send_email: e.target.checked })} data-testid="add-student-email" />
        Email the student a receipt / confirmation
      </label>
      <button onClick={submit} disabled={busy} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="add-student-submit">
        <UserPlus size={14} /> {busy ? "Adding…" : "Add to course"}
      </button>
    </Modal>
  );
}

export function AssignDateModal({ enrollment: e, onClose, onDone }) {
  const sessions = useSessions();
  const [sid, setSid] = useState("");
  const [override, setOverride] = useState(false);
  const [email, setEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const full = sid && isFull(sessions, sid);
  const submit = async () => {
    if (!sid) { toast.error("Pick a class date"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/enrollments/${e.id}/assign-session`, { class_session_id: sid, override_capacity: override, send_email: email });
      toast.success(e.class_session ? "Student moved to the new date" : "Class date assigned");
      onDone(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not assign date"); } finally { setBusy(false); }
  };
  return (
    <Modal title={e.class_session ? "Move to another date" : "Assign a class date"} onClose={onClose} testId="assign-date-modal">
      <div className="text-sm text-neutral-300">{e.student?.name} · <span className="text-white">{e.course?.title}</span>
        {e.class_session && <div className="text-xs text-neutral-500 mt-1">Currently: {fmtDay(e.class_session.starts_at)}</div>}</div>
      <SessionPicker sessions={sessions} courseId={e.course_id} value={sid} onChange={setSid} testId="assign-date-session" />
      {full && (
        <label className="flex items-center gap-2 text-xs text-orange-300" data-testid="assign-date-override">
          <input type="checkbox" checked={override} onChange={(ev) => setOverride(ev.target.checked)} /> This class is full (8/8) — add a 9th seat anyway
        </label>
      )}
      <label className="flex items-center gap-2 text-xs text-neutral-300">
        <input type="checkbox" checked={email} onChange={(ev) => setEmail(ev.target.checked)} data-testid="assign-date-email" /> Email the student the class details
      </label>
      <button onClick={submit} disabled={busy} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="assign-date-submit">{busy ? "Saving…" : "Confirm date"}</button>
    </Modal>
  );
}

export function RecordPaymentModal({ enrollment: e, onClose, onDone }) {
  const due = e.balance_due_cents || 0;
  const [f, setF] = useState({ method: "cash", amount: (due / 100).toFixed(2), note: "", send_email: true });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/enrollments/${e.id}/payment`, { ...f, amount_cents: Math.round(Number(f.amount) * 100) });
      toast.success(data.balance_paid ? "Balance paid in full" : `Recorded — ${money(data.balance_due_cents)} still due`);
      onDone(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not record payment"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Record payment taken in person" onClose={onClose} testId="record-payment-modal">
      <div className="text-sm text-neutral-300">{e.student?.name} · <span className="text-white">{e.course?.title}</span>
        <div className="text-xs mt-1">Balance due: <span className="text-[#FFD300] font-mono" data-testid="record-payment-due">{money(due)}</span></div></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={labelCls}>Method</label>
          <select value={f.method} onChange={(ev) => setF({ ...f, method: ev.target.value })} className={inputCls} data-testid="record-payment-method">
            {PAYMENT_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></div>
        <div><label className={labelCls}>Amount ($)</label>
          <input type="number" min="0.01" step="0.01" value={f.amount} onChange={(ev) => setF({ ...f, amount: ev.target.value })} className={inputCls} data-testid="record-payment-amount" /></div>
      </div>
      <div><label className={labelCls}>Note (optional)</label>
        <input value={f.note} onChange={(ev) => setF({ ...f, note: ev.target.value })} className={inputCls} data-testid="record-payment-note" /></div>
      <label className="flex items-center gap-2 text-xs text-neutral-300">
        <input type="checkbox" checked={f.send_email} onChange={(ev) => setF({ ...f, send_email: ev.target.checked })} data-testid="record-payment-email" /> Email the student a receipt
      </label>
      <button onClick={submit} disabled={busy || !(Number(f.amount) > 0)} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="record-payment-submit">{busy ? "Saving…" : "Record payment"}</button>
    </Modal>
  );
}

export function StatusSelect({ enrollment: e, onDone }) {
  const [busy, setBusy] = useState(false);
  const status = e.status || "scheduled";
  const change = async (next) => {
    if (next === status) return;
    if (next === "completed") {
      if (!window.confirm(`Mark ${e.student?.name || e.student?.email} as COMPLETED?`)) return;
      setBusy(true);
      try { await api.post(`/admin/enrollments/${e.id}/complete`); onDone(); } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
      return;
    }
    let note = "";
    if (next === "no_show" || next === "cancelled") {
      note = window.prompt(`${STATUS_LABELS[next]} — add a note (optional). The seat will be released.`, "") ?? null;
      if (note === null) return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/enrollments/${e.id}/status`, { status: next, note });
      toast.success(`Status set to ${STATUS_LABELS[next]}`);
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to update status"); } finally { setBusy(false); }
  };
  return (
    <select value={status} disabled={busy} onChange={(ev) => change(ev.target.value)}
      className={`font-mono text-[10px] tracking-[0.18em] uppercase px-2 py-1 border-0 cursor-pointer ${STATUS_CLASS[status] || STATUS_CLASS.scheduled}`}
      data-testid={`status-select-${e.id}`}>
      {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v} className="bg-black text-white">{l}</option>)}
    </select>
  );
}

export function PaymentCell({ enrollment: e }) {
  const due = e.balance_due_cents || 0;
  if (e.payment_type === "comp") return <span className="code-badge text-sky-300 border-sky-300/40" data-testid={`pay-${e.id}`}>Comp</span>;
  if (e.payment_type === "unpaid" && due > 0) return <span className="code-badge text-red-300 border-red-300/40" data-testid={`pay-${e.id}`}>Unpaid · {money(due)}</span>;
  if (due > 0 && !e.balance_paid) return <span className="code-badge text-[#FFD300] border-[#FFD300]/40" data-testid={`pay-${e.id}`}>Deposit · {money(due)} due</span>;
  return <span className="code-badge text-emerald-300 border-emerald-300/40" data-testid={`pay-${e.id}`}>Paid{e.balance_paid_method ? ` · ${e.balance_paid_method.replace("_", " ")}` : ""}</span>;
}
